/**
 * `?view=v1|v2` for the Sandboxes page, and which view a user sees given the
 * URL, their saved default, and the feature flags they have.
 *
 * @module features/sandboxes/sandboxesRouteSearch
 */

export type SandboxesView = 'v1' | 'v2';

export type SandboxesListSearch = {
  /** v1 | v2 switcher. Undefined defers to the saved default. */
  view?: SandboxesView;
  /** v2 live-sandboxes owner filter. Only `false` (All) is carried; undefined is Mine. */
  mine?: false;
  /** v2 live-sandboxes application filter (an application id). */
  app?: string;
};

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function validateSandboxesListSearch(search: Record<string, unknown>): SandboxesListSearch {
  return {
    view: search.view === 'v1' || search.view === 'v2' ? search.view : undefined,
    mine: search.mine === false || search.mine === 'false' ? false : undefined,
    app: typeof search.app === 'string' && UUID.test(search.app) ? search.app : undefined,
  };
}

/**
 * The view to show: the URL's, else the saved default, each only when the user
 * has that view's flag; a user with one flag always gets that view. An org with
 * both flags but no v1 templates has nothing to show in v1, so it gets v2 only
 * (v1 is not offered to an org that has never used it).
 * Null when the user has neither.
 */
export function resolveSandboxesView(options: {
  requested: SandboxesView | undefined;
  savedDefault: SandboxesView;
  hasV1: boolean;
  hasV2: boolean;
  /** Whether v1 should be offered: the org has a v1 template, or the templates could not be read (fail open). Only consulted when the user has both flags. */
  hasV1Templates: boolean;
}): SandboxesView | null {
  const { requested, savedDefault, hasV1, hasV2, hasV1Templates } = options;
  if (!hasV1 && !hasV2) return null;
  if (hasV1 && !hasV2) return 'v1';
  if (hasV2 && !hasV1) return 'v2';
  if (!hasV1Templates) return 'v2';
  return requested ?? savedDefault;
}
